import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ToastContainer } from '../components/shared/ToastContainer';
import { ToastContext, type ToastType } from './toastContext';

type ToastState = {
  message: ReactNode;
  type: ToastType;
  id: number;
  leaving: boolean;
} | null;

let toastIdCounter = 0;

/** toast-fade-out アニメーション (toast.css) と同期させること */
const LEAVE_DURATION_MS = 200;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastState>(null);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const leaveTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const currentIdRef = useRef(0);
  // ホバー一時停止用: 自動消滅の締切と残り時間 (0 = 自動消滅なし)
  const deadlineRef = useRef(0);
  const pausedRemainingRef = useRef(0);

  const clearTimers = useCallback(() => {
    clearTimeout(dismissTimerRef.current);
    clearTimeout(leaveTimerRef.current);
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  const beginHide = useCallback((id: number) => {
    if (currentIdRef.current !== id) return;
    clearTimeout(dismissTimerRef.current);
    deadlineRef.current = 0;
    setToast((current) => (current?.id === id ? { ...current, leaving: true } : current));
    clearTimeout(leaveTimerRef.current);
    leaveTimerRef.current = setTimeout(() => {
      setToast((current) => (current?.id === id ? null : current));
    }, LEAVE_DURATION_MS);
  }, []);

  const hide = useCallback(() => beginHide(currentIdRef.current), [beginHide]);

  const startDismissTimer = useCallback(
    (id: number, ms: number) => {
      deadlineRef.current = Date.now() + ms;
      dismissTimerRef.current = setTimeout(() => beginHide(id), ms);
    },
    [beginHide],
  );

  const showToast = useCallback(
    (msg: ReactNode, type: ToastType, duration = 6000) => {
      clearTimers();
      deadlineRef.current = 0;
      pausedRemainingRef.current = 0;
      const id = ++toastIdCounter;
      currentIdRef.current = id;
      setToast({ message: msg, type, id, leaving: false });
      if (duration > 0) startDismissTimer(id, duration);
    },
    [clearTimers, startDismissTimer],
  );

  /** ホバー中は自動消滅を止め、離れたら残り時間で再開する */
  const pauseDismiss = useCallback(() => {
    if (deadlineRef.current === 0) return;
    clearTimeout(dismissTimerRef.current);
    pausedRemainingRef.current = Math.max(deadlineRef.current - Date.now(), 1000);
    deadlineRef.current = 0;
  }, []);

  const resumeDismiss = useCallback(() => {
    if (pausedRemainingRef.current === 0) return;
    startDismissTimer(currentIdRef.current, pausedRemainingRef.current);
    pausedRemainingRef.current = 0;
  }, [startDismissTimer]);

  const show = useCallback((msg: ReactNode, duration?: number) => showToast(msg, 'default', duration), [showToast]);
  const success = useCallback((msg: ReactNode, duration?: number) => showToast(msg, 'success', duration), [showToast]);
  const error = useCallback((msg: ReactNode, duration = 30000) => showToast(msg, 'error', duration), [showToast]);

  const value = useMemo(() => ({ show, success, error }), [show, success, error]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer
        message={toast?.message ?? null}
        type={toast?.type ?? 'default'}
        leaving={toast?.leaving ?? false}
        onClose={hide}
        onPointerEnter={pauseDismiss}
        onPointerLeave={resumeDismiss}
      />
    </ToastContext.Provider>
  );
}
